import React, { useEffect, useState } from "react";
import Header from "../components/Header";
import BlogPage from "../components/Blogs";
import Footer from "../components/Footer";
import CTA from "../components/CTA";
import ScrollToTopButton from "../components/ScrollTop";
import SEO from "../components/SEO";
import WhatsAppMsg from "../components/WhatsApp";
import { Helmet } from "react-helmet";
import { blogData } from "../utils/blogData";
import { formatDateDDMMMYYYY } from "../utils/dateHelpers";

const Blogs = () => {
  const [blogs, setBlogs] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await blogData();
        setBlogs(data);
      } catch (error) {
        console.error("Error fetching blogs:", error);
      }
    };

    fetchData();
  }, []);

  // Sort blogs by date (newest first).
  // Ensure that the blog objects include a valid 'date' property.
  const sortedBlogData = [...blogs].sort(
    (a, b) =>
      new Date(formatDateDDMMMYYYY(b.created_at)) -
      new Date(formatDateDDMMMYYYY(a.created_at))
  );
  // Generate current date in YYYY-MM-DD format
  const currentDate = new Date().toISOString().split("T")[0];

  return (
    <>
      <div className="blogs-page">
        <SEO
          title="Educational & Corporate Technology Insights | Coltec Blogs"
          description="Explore the latest trends, tips, and innovations in educational and corporate technology. Learn how Coltec's interactive solutions are transforming classrooms."
          keywords="Interactive flat panels, Education technology blogs, Corporate technology insights, Classroom collaboration tools, Digital whiteboards, Smart boards, Interactive displays, Touch screen panels for business, Modern learning technology, Workplace innovation blogs, Technology transformation, EdTech, Corporate collaboration solutions"
          ogTitle="Educational & Corporate Technology Innovations | Coltec Blogs"
          ogDescription="Stay ahead with Coltec's blogs on cutting-edge educational and corporate technology. Discover insights, tips, and solutions for modern learning and business environments."
          ogImage="https://content.qeeb.in/coltec/blogs/section-blog-3.webp"
          ogUrl="https://coltec-global.com/blogs"
          canonicalUrl="https://coltec-global.com/blogs"
          ogImageAlt="Coltec Blogs - Technology Innovations in Education and Corporate"
          ogSiteName="Coltec Global"
          ogType="website"
          twitterSite="https://x.com/ColtecGlobal"
          twitterTitle="Educational & Corporate Technology Insights | Coltec Blogs"
          twitterDescription="Explore the latest trends, tips, and innovations in educational and corporate technology. Learn how Coltec's interactive solutions are transforming classrooms."
          twitterImage="https://content.qeeb.in/coltec/blogs/section-blog-3.webp"
          twitterCard="summary"
          geoRegion="IN"
        />
        {/* JSON‑LD Structured Data with Publisher & Address Details */}
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify(
              {
                "@context": "https://schema.org",
                "@graph": [
                  // Organization details
                  {
                    "@type": "Organization",
                    "@id": "https://coltec-global.com/#organization",
                    name: "COLTEC Global",
                    aggregateRating: {
                      "@type": "AggregateRating",
                      ratingValue: "4.8",
                      reviewCount: "350",
                    },
                    description:
                      "COLTEC Global is a leading manufacturer of Visual Interactive Display Technology, delivering innovative solutions for Education, Corporate, and Retail applications.",
                    url: "https://coltec-global.com",
                    logo: {
                      "@type": "ImageObject",
                      url: "https://content.qeeb.in/coltec/coltec-logo-black.svg",
                    },
                    address: [
                      {
                        "@type": "PostalAddress",
                        streetAddress:
                          "37, Survey No 75/A, Gagan Pahad Village, Raghavendra Nagar",
                        addressLocality: "Hyderabad",
                        addressRegion: "Telangana",
                        postalCode: "500030",
                        addressCountry: "IN",
                      },
                      {
                        "@type": "PostalAddress",
                        streetAddress:
                          "COLTEC GLOBAL #41-14-8/2, Opp Mouli Towers, Near Jyothi Convention Hall,Benz circle",
                        addressLocality: "Vijayawada",
                        addressRegion: "Andhra Pradesh",
                        postalCode: "520 010",
                        addressCountry: "IN",
                      },
                    ],
                    sameAs: [
                      "https://www.facebook.com/profile.php?id=61570213181020",
                      "https://x.com/ColtecGlobal",
                      "https://www.linkedin.com/company/coltec-global/",
                      "https://www.youtube.com/@coltecglobal5339",
                      "https://www.google.com/maps/place/COLTEC+GLOBAL+INDIA/@17.3297791,78.4381149,17z/data=!3m1!4b1!4m6!3m5!1s0x3bcbbd8bb97fefcd:0x2536a4697f2f1423!8m2!3d17.3297791!4d78.4406898!16s%2Fg%2F11w1dth9rd?entry=ttu&g_ep=EgoyMDI1MDIyNi4xIKXMDSoJLDEwMjExNDUzSAFQAw%3D%3D",
                    ],
                    email: "sales@coltec-global.com",
                    contactPoint: [
                      {
                        "@type": "ContactPoint",
                        telephone: "+91 1800 309 8822",
                        contactType: "customer support",
                      },
                    ],
                  },
                  // WebSite details
                  {
                    "@type": "WebSite",
                    "@id": "https://coltec-global.com/#website",
                    url: "https://coltec-global.com",
                    name: "COLTEC Global",
                    publisher: {
                      "@id": "https://coltec-global.com/#organization",
                    },
                    potentialAction: {
                      "@type": "SearchAction",
                      target: `https://coltec-global.com/blogs?s={search_term_string}`,
                      "query-input": "required name=search_term_string",
                    },
                    inLanguage: "en",
                  },
                  // Updated Product details with additional properties
                  {
                    "@type": "Blog",
                    name: "Educational & Corporate Technology Insights | Coltec Blogs",
                    url: "https://coltec-global.com/blogs",
                    blogPost: sortedBlogData.map((blog) => ({
                      "@type": "BlogPosting",
                      headline: blog.title,
                      url: `https://coltec-global.com/blogs/${blog.slug}`,
                      datePublished:
                        formatDateDDMMMYYYY(blog.published_at) ||
                        formatDateDDMMMYYYY(blog.created_at),
                      dateModified: currentDate,
                      author: {
                        "@type": "Person",
                        name: blog.author_name,
                      },
                    })),
                    review: sortedBlogData.map((blog) => ({
                      "@type": "Review",
                      headline: blog.title,
                      itemReviewed: {
                        "@type": "Product",
                        name: blog.title,
                      },
                      author: {
                        "@type": "Person",
                        name: blog.author_name,
                      },
                      reviewBody:
                        blog.metaDescription ||
                        "No review description available.",
                      reviewRating: {
                        "@type": "Rating",
                        ratingValue: "4.5",
                        bestRating: "5",
                        worstRating: "1",
                      },
                      datePublished:
                      formatDateDDMMMYYYY(blog.published_at) ||
                      formatDateDDMMMYYYY(blog.created_at),
                      dateModified: currentDate,
                      publisher: {
                        "@type": "Organization",
                        name: "COLTEC Global",
                      },
                    })),
                  },
                  // Breadcrumb for Product Details page
                  {
                    "@type": "BreadcrumbList",
                    itemListElement: [
                      {
                        "@type": "ListItem",
                        position: 1,
                        name: "Home",
                        item: "https://coltec-global.com",
                      },
                      {
                        "@type": "ListItem",
                        position: 2,
                        name: "Blogs",
                        item: "https://coltec-global.com/blogs",
                      },
                    ],
                  },
                  {
                    "@type": "LocalBusiness",
                    "@id": "https://coltec-global.com/#localbusiness",
                    name: "COLTEC Global",
                    url: "https://coltec-global.com",
                    image:
                      "https://content.qeeb.in/coltec/coltec-logo-black.svg",
                    address: {
                      "@type": "PostalAddress",
                      streetAddress:
                        "37, Survey No 75/A, Gagan Pahad Village, Raghavendra Nagar",
                      addressLocality: "Hyderabad",
                      addressRegion: "Telangana",
                      postalCode: "500030",
                      addressCountry: "IN",
                    },
                    telephone: "+91 1800 309 8822",
                    openingHours: "Mon-Sat 09:00-6:00",
                    geo: {
                      "@type": "GeoCoordinates",
                      latitude: "17.3297791",
                      longitude: "78.4381149",
                    },
                    sameAs: [
                      "https://www.facebook.com/profile.php?id=61570213181020",
                      "https://x.com/ColtecGlobal",
                      "https://www.linkedin.com/company/coltec-global/",
                      "https://www.youtube.com/@coltecglobal5339",
                      "https://www.google.com/maps/place/COLTEC+GLOBAL+INDIA/@17.3297791,78.4381149,17z/data=!3m1!4b1!4m6!3m5!1s0x3bcbbd8bb97fefcd:0x2536a4697f2f1423!8m2!3d17.3297791!4d78.4406898!16s%2Fg%2F11w1dth9rd?entry=ttu&g_ep=EgoyMDI1MDIyNi4xIKXMDSoJLDEwMjExNDUzSAFQAw%3D%3D",
                    ],
                  },
                ],
              },
              null,
              2
            )}
          </script>
        </Helmet>
        <Header />
        <div className="md:mt-16 mt-12">
          <BlogPage />
        </div>
        <CTA />
        <Footer />
        <ScrollToTopButton />
        <WhatsAppMsg />
      </div>
    </>
  );
};

export default Blogs;
